import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Bookmark, Check, ChevronDown, ChevronRight, Pencil, Plus, Trash2, X } from 'lucide-react';

/**
 * Sidebar Collections (Phase 8 §3 E).
 *
 * Pinned requisitions (or other records) surfaced as quick-links. Persisted in
 * localStorage under `ggj.sidebar.collections.v1`. Max 8 pinned entries.
 *
 * Features:
 *  - Accordion: click the "COLLECTIONS" header to collapse / expand the list.
 *    Open state is persisted per-session so a reload respects the last choice.
 *  - Per-item actions on hover: [✎ rename] and [🗑 delete].
 *    Rename switches the item into an inline editable input without any modal.
 *    Delete removes the item (previously required a long-press / context menu).
 */
const LS_KEY       = 'ggj.sidebar.collections.v1';
const LS_OPEN_KEY  = 'ggj.sidebar.collections.open';
const MAX_ITEMS    = 8;

export type SidebarCollectionItem = {
  id: string;
  title: string;
  /** Destination path. Defaults to `/requisitions/:id` when omitted. */
  to?: string;
};

// ── persistence helpers ────────────────────────────────────────────────────

function readCollections(): SidebarCollectionItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (v) =>
          v &&
          typeof v === 'object' &&
          typeof v.id === 'string' &&
          typeof v.title === 'string',
      )
      .slice(0, MAX_ITEMS) as SidebarCollectionItem[];
  } catch {
    return [];
  }
}

function writeCollections(items: SidebarCollectionItem[]) {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
  } catch {
    /* ignore storage errors */
  }
}

function readOpen(): boolean {
  try {
    return window.localStorage.getItem(LS_OPEN_KEY) !== '0';
  } catch {
    return true;
  }
}

function writeOpen(open: boolean) {
  try {
    window.localStorage.setItem(LS_OPEN_KEY, open ? '1' : '0');
  } catch {
    /* ignore */
  }
}

// ── public API ─────────────────────────────────────────────────────────────

export function pinCollection(item: SidebarCollectionItem): boolean {
  const items = readCollections();
  if (items.some((i) => i.id === item.id)) return false;
  if (items.length >= MAX_ITEMS) return false;
  writeCollections([...items, item]);
  window.dispatchEvent(new CustomEvent('ggj:collections-changed'));
  return true;
}

export function unpinCollection(id: string) {
  writeCollections(readCollections().filter((i) => i.id !== id));
  window.dispatchEvent(new CustomEvent('ggj:collections-changed'));
}

export function renameCollection(id: string, newTitle: string) {
  writeCollections(
    readCollections().map((i) => (i.id === id ? { ...i, title: newTitle.trim() || i.title } : i)),
  );
  window.dispatchEvent(new CustomEvent('ggj:collections-changed'));
}

// ── component ─────────────────────────────────────────────────────────────

interface SidebarCollectionsProps {
  onAddClick?: () => void;
}

export function SidebarCollections({ onAddClick }: SidebarCollectionsProps) {
  const [items, setItems]           = useState<SidebarCollectionItem[]>(() => readCollections());
  const [open, setOpen]             = useState<boolean>(() => readOpen());
  /** ID of the item currently being renamed inline. */
  const [editingId, setEditingId]   = useState<string | null>(null);
  const [editValue, setEditValue]   = useState('');
  const editInputRef                = useRef<HTMLInputElement | null>(null);

  // Sync from localStorage events (cross-tab + same-tab via custom event).
  useEffect(() => {
    const sync = () => setItems(readCollections());
    window.addEventListener('ggj:collections-changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('ggj:collections-changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // Focus the rename input when it mounts.
  useEffect(() => {
    if (editingId) editInputRef.current?.focus();
  }, [editingId]);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => {
      writeOpen(!prev);
      return !prev;
    });
  }, []);

  const handleAddClick = useCallback(() => {
    if (onAddClick) {
      onAddClick();
      return;
    }
    console.log('[SidebarCollections] TODO(T-03): open pin-from-requisitions modal');
  }, [onAddClick]);

  const startRename = useCallback((item: SidebarCollectionItem) => {
    setEditingId(item.id);
    setEditValue(item.title);
  }, []);

  const commitRename = useCallback(
    (id: string) => {
      renameCollection(id, editValue);
      setEditingId(null);
    },
    [editValue],
  );

  const cancelRename = useCallback(() => setEditingId(null), []);

  const handleDelete = useCallback((id: string) => {
    unpinCollection(id);
  }, []);

  const atLimit = useMemo(() => items.length >= MAX_ITEMS, [items.length]);

  return (
    <div className="mt-3 px-3">
      {/* ── Header row: label + chevron (accordion toggle) + add button ── */}
      <div className="flex items-center justify-between px-2 mb-1">
        <button
          type="button"
          onClick={toggleOpen}
          className="flex flex-1 items-center gap-1 text-left"
          aria-expanded={open}
          aria-controls="sidebar-collections-list"
        >
          {open ? (
            <ChevronDown
              className="h-3 w-3 shrink-0 opacity-50"
              aria-hidden
            />
          ) : (
            <ChevronRight
              className="h-3 w-3 shrink-0 opacity-50"
              aria-hidden
            />
          )}
          <span className="text-[11px] font-semibold uppercase tracking-[0.06em] vscode-text-tertiary">
            Collections
          </span>
          {items.length > 0 && (
            <span
              className="ml-1 rounded-full px-1.5 text-[10px] font-medium"
              style={{
                background: 'var(--vscode-bg-hover)',
                color: 'var(--vscode-text-tertiary)',
              }}
            >
              {items.length}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={handleAddClick}
          disabled={atLimit}
          className="rounded-md p-1 vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={atLimit ? `Max ${MAX_ITEMS} collections reached` : 'Pin a requisition'}
          title={atLimit ? `Max ${MAX_ITEMS} collections` : 'Pin a requisition'}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      {/* ── Accordion body ── */}
      {open && (
        <div
          id="sidebar-collections-list"
          className="flex flex-col gap-0.5"
        >
          {items.length === 0 ? (
            <p className="px-2 py-1 text-[11px] italic vscode-text-tertiary">
              No collections yet.
            </p>
          ) : (
            items.map((item) => {
              const to = item.to ?? `/requisitions/${item.id}`;
              const isEditing = editingId === item.id;

              return (
                <div key={item.id} className="group relative">
                  {isEditing ? (
                    /* ── Inline rename row ── */
                    <div className="flex h-[30px] items-center gap-1 rounded-[8px] px-2">
                      <Bookmark className="h-4 w-4 shrink-0 opacity-40" aria-hidden />
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename(item.id);
                          if (e.key === 'Escape') cancelRename();
                        }}
                        className="min-w-0 flex-1 rounded bg-transparent text-[12px] outline-none ring-1 ring-emerald-500/60 px-1"
                        style={{ color: 'var(--vscode-text-primary)' }}
                        aria-label="Rename collection"
                      />
                      <button
                        type="button"
                        onClick={() => commitRename(item.id)}
                        className="shrink-0 rounded p-0.5 text-emerald-600 hover:bg-emerald-500/10"
                        aria-label="Save rename"
                      >
                        <Check className="h-3.5 w-3.5" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={cancelRename}
                        className="shrink-0 rounded p-0.5 vscode-text-tertiary hover:vscode-bg-hover"
                        aria-label="Cancel rename"
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </div>
                  ) : (
                    /* ── Normal nav row with hover actions ── */
                    <div className="flex h-[30px] items-center rounded-[8px]">
                      <NavLink
                        to={to}
                        className={({ isActive }) =>
                          [
                            'flex min-w-0 flex-1 h-full items-center gap-2 rounded-[8px] px-2 text-[13px] font-medium transition-colors',
                            isActive
                              ? 'vscode-bg-tertiary vscode-text-primary'
                              : 'vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary',
                          ].join(' ')
                        }
                        title={item.title}
                      >
                        <Bookmark className="h-4 w-4 flex-shrink-0" aria-hidden />
                        <span className="min-w-0 flex-1 truncate">{item.title}</span>
                      </NavLink>

                      {/* Hover action buttons — edit + delete */}
                      <div className="flex shrink-0 items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity pr-1">
                        <button
                          type="button"
                          onClick={() => startRename(item)}
                          className="rounded p-1 vscode-text-tertiary hover:vscode-bg-hover hover:text-blue-500 transition-colors"
                          aria-label={`Rename "${item.title}"`}
                          title="Rename"
                        >
                          <Pencil className="h-3 w-3" aria-hidden />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          className="rounded p-1 vscode-text-tertiary hover:vscode-bg-hover hover:text-red-500 transition-colors"
                          aria-label={`Delete "${item.title}"`}
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3" aria-hidden />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
